<template>
  <div v-if="leaderTableVisible" class="leaderboard-wrapper">
    <!-- Mobile Collapsed View -->
    <div class="leaderboard-mobile-card" @click="toggleExpand">
      <div class="leaderboard-mobile-header">
        <div class="leaderboard-mobile-info">
          <div class="leaderboard-mobile-icon">
            <i class="uil-trophy"></i>
          </div>
          <div class="leaderboard-mobile-text">
            <span v-if="leaderBoardTitle" class="leaderboard-mobile-title">{{ leaderBoardTitle }}</span>
            <!-- Skeleton for rank -->
            <span v-if="!leader_board || !leader_board.result" class="leaderboard-mobile-rank-skeleton"></span>
            <span v-else class="leaderboard-mobile-rank">{{ getCurrentUserPosition() }}. {{ current_user?.name + ' ' + current_user?.surname }}</span>
          </div>
        </div>
        <button
          class="lb-toggle-btn"
          :class="{ 'lb-toggle-btn--active': isExpanded }"
          type="button"
          :aria-expanded="isExpanded"
          :aria-label="isExpanded ? 'Detayları kapat' : 'Detayları aç'"
          @click.stop="toggleExpand"
        >
          <i class="uil-angle-down"></i>
        </button>
      </div>

      <!-- Mobile Expanded Content -->
      <div v-show="isExpanded" class="leaderboard-mobile-content" @click.stop>
          <!-- Skeleton Loading -->
          <div v-if="!leader_board || !leader_board.result" class="pt-2 pb-1">
            <div class="skeleton-leaderboard-mobile">
              <div v-for="n in 5" :key="'skeleton_leader_mobile_' + n" class="skeleton-mobile-item">
                <div class="skeleton-mobile-left">
                  <div class="skeleton-mobile-rank"></div>
                  <div class="skeleton-mobile-avatar"></div>
                  <div class="skeleton-mobile-name"></div>
                </div>
                <div class="skeleton-mobile-points"></div>
              </div>
            </div>
          </div>
          <!-- Actual Content -->
          <div v-else class="pt-2 pb-1">
            <div
              v-if="mobileTopThree && topThreeUsers.length"
              class="leaderboard-mobile-podium"
              role="list"
            >
              <div
                v-for="(user_point, index) in topThreeUsers"
                :key="'leader_mobile_podium_' + index"
                :class="[
                  'leaderboard-mobile-podium__item--rank-' + (index + 1),
                  { 'leaderboard-mobile-podium__item--active': user_point.id == current_user?.id }
                ]"
                class="leaderboard-mobile-podium__item"
                role="listitem"
              >
                <div class="leaderboard-mobile-podium__avatar-stage">
                  <div class="leaderboard-mobile-podium__avatar-wrap">
                    <UserProfileAvatar
                      :name="user_point.fullname || ''"
                      :photo="user_point.photo_link || ''"
                      class="leaderboard-mobile-podium__avatar"
                    />
                  </div>
                  <span class="leaderboard-mobile-podium__rank" aria-hidden="true">{{ index + 1 }}</span>
                </div>
                <span class="leaderboard-mobile-podium__name" :title="user_point.fullname">
                  {{ user_point.fullname }}
                </span>
                <span class="leaderboard-mobile-podium__points">
                  {{ formatPoints(user_point.points_sum_point) }}
                  <small>{{ $t('home.point') }}</small>
                </span>
              </div>
            </div>

            <ul
              v-if="!mobileTopThree || remainingMobileUsers.length"
              id="leadership-mobile"
              :class="{ 'leaderboard-mobile-list--after-podium': mobileTopThree }"
              class="uk-list-divider mt-2"
            >
              <template v-for="(user_point, index) in remainingMobileUsers">
                <li :key="'leader_mobile_' + index" :class="{ active: user_point.id == current_user?.id }"
                    class="d-flex justify-content-between align-items-center">
                  <span class="d-flex align-items-center">
                    <span class="line">{{ getMobileUserRank(index) }}</span>
                    <UserProfileAvatar
                      :name="user_point.fullname || ''"
                      :photo="user_point.photo_link || ''"
                      class="leaderboard-avatar"
                    />
                    <span class="text-truncate">{{ user_point.fullname }}</span>
                  </span>
                  <span class="points-value">{{ user_point.points_sum_point ? user_point.points_sum_point : 0 }} {{ $t('home.point') }}</span>
                </li>
              </template>
            </ul>
          </div>
        </div>
    </div>

    <!-- Desktop View (Original) -->
    <div class="leaderboard-desktop uk-card-default rounded">
      <div class="course-card-body">
        <div v-if="leaderBoardTitle" class="uk-card-header pb-2 pt-0 pl-0 pr-0">
          <div class="section-header-left">
            <h4 class="uk-text-truncate"><i class="uil-trophy"></i> {{ leaderBoardTitle }}</h4>
          </div>
        </div>

        <!-- Skeleton Loading -->
        <div v-if="!leader_board || !leader_board.result" class="pt-2 pb-1 text-center">
          <div class="skeleton-leaderboard">
            <!-- Current user skeleton -->
            <div class="skeleton-leaderboard-item yourself">
              <div class="skeleton-avatar"></div>
              <div class="skeleton-text"></div>
              <div class="skeleton-points"></div>
            </div>
            <!-- Other users skeleton -->
            <div v-for="n in 5" :key="'skeleton_leader_' + n" class="skeleton-leaderboard-item">
              <div class="skeleton-rank"></div>
              <div class="skeleton-avatar"></div>
              <div class="skeleton-text"></div>
              <div class="skeleton-points"></div>
            </div>
          </div>
        </div>

        <!-- Actual Content -->
        <div v-else class="pt-2 pb-1">
          <ul id="leadership" class="uk-list-divider mt-2">
            <li class="yourself">
              <span class="d-flex align-items-center">
                <span class="line">{{ getCurrentUserPosition() }}. {{ current_user?.name + ' ' + current_user?.surname }}</span>
              </span>
              <span class="points-value">{{ dashboard_item?.points_count || 0 }} {{ $t('home.point') }}</span>
            </li>
            <template v-for="(user_point, index) in leader_board?.result || []">
              <li :key="'leader_desktop_' + index" :class="{ active: user_point.id == current_user?.id }">
                <span class="d-flex align-items-center">
                  <span class="line">{{ index + 1 }}</span>
                  <UserProfileAvatar
                    :name="user_point.fullname || ''"
                    :photo="user_point.photo_link || ''"
                    class="leaderboard-avatar"
                  />
                  <span class="user-name">{{ user_point.fullname }}</span>
                </span>
                <span class="points-value">{{ user_point.points_sum_point ? user_point.points_sum_point : 0 }} {{ $t('home.point') }}</span>
              </li>
            </template>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import UserProfileAvatar from "@/view/components/common/UserProfileAvatar.vue";
export default {
  name: "LeaderboardCard",
  components: {
    UserProfileAvatar,
  },
  data() {
    return {
      isExpanded: false
    };
  },
  props: {
    leaderTableVisible: {
      type: Boolean,
      default: true
    },
    leaderBoardTitle: {
      type: String,
      default: ''
    },
    current_user: {
      type: Object,
      required: false,
      default: () => ({})
    },
    leader_board: {
      type: Object,
      required: false,
      default: () => ({})
    },
    dashboard_item: {
      type: Object,
      required: false,
      default: () => ({})
    },
    mobileTopThree: {
      type: Boolean,
      default: false
    },
  },
  computed: {
    mobileUsers() {
      return Array.isArray(this.leader_board?.result) ? this.leader_board.result : [];
    },
    topThreeUsers() {
      return this.mobileTopThree ? this.mobileUsers.slice(0, 3) : [];
    },
    remainingMobileUsers() {
      return this.mobileTopThree ? this.mobileUsers.slice(3) : this.mobileUsers;
    }
  },
  methods: {
    getCurrentUserPosition() {
      return Number(this.leader_board?.userIndex) || 0;
    },
    toggleExpand() {
      this.isExpanded = !this.isExpanded;
    },
    getMobileUserRank(index) {
      return index + (this.mobileTopThree ? 4 : 1);
    },
    formatPoints(points) {
      const numericPoints = Number(points) || 0;
      return numericPoints.toLocaleString(this.$i18n?.locale || 'tr-TR');
    }
  }
};
</script>

<style scoped>
/* Mobile Card Styles */
.leaderboard-mobile-card {
  display: none;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-top: 12px;
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}

.leaderboard-mobile-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  gap: 12px;
}

.leaderboard-mobile-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.leaderboard-mobile-icon {
  width: 40px;
  height: 40px;
  min-width: 40px;
  background: #eef2ff;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.leaderboard-mobile-icon i {
  font-size: 20px;
  color: #666cff;
}

.leaderboard-mobile-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.leaderboard-mobile-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.leaderboard-mobile-rank {
  font-size: 12px;
  color: #666;
  font-weight: 500;
}

.leaderboard-mobile-rank-skeleton {
  display: inline-block;
  width: 80px;
  height: 14px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
}

.lb-toggle-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  min-width: 48px;
  height: 48px;
  min-height: 48px;
  padding: 0 !important;
  background: transparent !important;
  border: 0 !important;
  border-radius: 50%;
  outline: none !important;
  box-shadow: none !important;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  transition: background-color 160ms ease, box-shadow 160ms ease;
}

.lb-toggle-btn:active {
  background: #f2f4f7 !important;
}

.lb-toggle-btn:focus {
  outline: none !important;
}

.lb-toggle-btn:focus-visible {
  box-shadow: 0 0 0 3px rgba(102, 108, 255, 0.14) !important;
}

.lb-toggle-btn i {
  display: inline-flex;
  width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: #475467 !important;
  font-size: 30px;
  line-height: 1;
  transition: color 160ms ease, transform 200ms ease;
}

.lb-toggle-btn--active {
  background: transparent !important;
}

.lb-toggle-btn--active i {
  transform: rotate(180deg);
  background: transparent;
  color: #3f459d !important;
}

@media (hover: hover) {
  .lb-toggle-btn:hover {
    background: #f2f4f7 !important;
    box-shadow: none !important;
  }

  .lb-toggle-btn--active:hover {
    background: #f2f4f7 !important;
  }
}

.leaderboard-mobile-content {
  border-top: 1px solid #f0f0f0;
  padding: 0 16px 16px;
  cursor: default;
}

/* Mobile top-three podium (enabled by the Softinya home page). */
.leaderboard-mobile-podium {
  position: relative;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: start;
  gap: 4px;
  min-height: 210px;
  margin: 4px 0 16px;
  padding: 12px 6px 14px;
  overflow: hidden;
  border-radius: 14px;
  border: 1px solid #e6eaf0;
  background:
    radial-gradient(circle at 50% 4%, rgba(79, 70, 229, 0.035) 0 50px, transparent 51px),
    linear-gradient(180deg, #fff 0%, #fafbfc 100%);
}

.leaderboard-mobile-podium__item {
  position: relative;
  display: flex;
  min-width: 0;
  padding: 50px 2px 0;
  flex-direction: column;
  align-items: center;
  color: #17192b;
}

.leaderboard-mobile-podium__item--rank-1 {
  grid-column: 2;
  grid-row: 1;
  padding-top: 18px;
}

.leaderboard-mobile-podium__item--rank-2 {
  grid-column: 1;
  grid-row: 1;
}

.leaderboard-mobile-podium__item--rank-3 {
  grid-column: 3;
  grid-row: 1;
}

.leaderboard-mobile-podium__avatar-stage {
  position: relative;
  display: flex;
  width: 72px;
  height: 72px;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.leaderboard-mobile-podium__avatar-stage::before,
.leaderboard-mobile-podium__avatar-stage::after {
  position: absolute;
  content: '';
  border-radius: 50%;
  pointer-events: none;
}

.leaderboard-mobile-podium__avatar-stage::before {
  inset: -7px;
  border: 1px solid rgba(100, 116, 139, 0.17);
}

.leaderboard-mobile-podium__avatar-stage::after {
  inset: -13px;
  border: 1px solid rgba(100, 116, 139, 0.07);
}

.leaderboard-mobile-podium__item--rank-1 .leaderboard-mobile-podium__avatar-stage {
  width: 94px;
  height: 94px;
  margin-bottom: 23px;
}

.leaderboard-mobile-podium__item--rank-1 .leaderboard-mobile-podium__avatar-stage::before {
  border-color: rgba(180, 134, 24, 0.18);
}

.leaderboard-mobile-podium__item--rank-1 .leaderboard-mobile-podium__avatar-stage::after {
  border-color: rgba(180, 134, 24, 0.07);
}

.leaderboard-mobile-podium__item--rank-3 .leaderboard-mobile-podium__avatar-stage::before {
  border-color: rgba(161, 98, 58, 0.15);
}

.leaderboard-mobile-podium__rank {
  position: absolute;
  z-index: 4;
  bottom: -13px;
  left: 50%;
  display: inline-flex;
  width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  transform: translateX(-50%);
  border: 3px solid #fff;
  border-radius: 50%;
  background: #7c879a;
  box-shadow: 0 3px 7px rgba(53, 61, 80, 0.16);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
}

.leaderboard-mobile-podium__item--rank-1 .leaderboard-mobile-podium__rank {
  bottom: -15px;
  width: 38px;
  height: 38px;
  background: #c69524;
  box-shadow: 0 4px 8px rgba(148, 108, 17, 0.2);
  font-size: 15px;
}

.leaderboard-mobile-podium__item--rank-3 .leaderboard-mobile-podium__rank {
  background: #a86843;
  box-shadow: 0 3px 7px rgba(120, 69, 39, 0.18);
}

.leaderboard-mobile-podium__avatar-wrap {
  position: relative;
  z-index: 2;
  display: flex;
  width: 100%;
  height: 100%;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border: 2px solid #aeb6c5;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 4px 12px rgba(30, 41, 59, 0.1);
}

.leaderboard-mobile-podium__item--rank-1 .leaderboard-mobile-podium__avatar-wrap {
  border-color: #c69524;
}

.leaderboard-mobile-podium__item--rank-3 .leaderboard-mobile-podium__avatar-wrap {
  border-color: #a86843;
}

.leaderboard-mobile-podium__item--active .leaderboard-mobile-podium__avatar-wrap {
  box-shadow: 0 0 0 2px rgba(79, 70, 229, 0.15), 0 4px 12px rgba(30, 41, 59, 0.1);
}

.leaderboard-mobile-podium__avatar {
  width: 100%;
  height: 100%;
  min-width: 0;
  font-size: 16px;
}

.leaderboard-mobile-podium__item--rank-1 .leaderboard-mobile-podium__avatar {
  font-size: 20px;
}

.leaderboard-mobile-podium__name {
  display: block;
  width: 100%;
  overflow: hidden;
  color: #17192b;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.3;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.leaderboard-mobile-podium__item--rank-1 .leaderboard-mobile-podium__name {
  font-size: 14px;
}

.leaderboard-mobile-podium__points {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 4px;
  margin-top: 5px;
  color: #4338a8;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.25;
  white-space: nowrap;
}

.leaderboard-mobile-podium__points small {
  color: #73768a;
  font-size: 10px;
  font-weight: 400;
}

.leaderboard-mobile-podium__item--rank-1 .leaderboard-mobile-podium__points {
  font-size: 17px;
}

#leadership-mobile.leaderboard-mobile-list--after-podium {
  margin-top: 0 !important;
}

/* Desktop view - default visible */
.leaderboard-desktop {
  display: block;
}

/* Responsive breakpoints */
@media (max-width: 768px) {
  .leaderboard-mobile-card {
    display: block;
  }

  .leaderboard-desktop {
    display: none;
  }
}

@media (max-width: 480px) {
  .leaderboard-mobile-header {
    align-items: center;
    padding: 12px;
    gap: 8px;
  }

  .leaderboard-mobile-info {
    gap: 8px;
  }

  .leaderboard-mobile-icon {
    width: 36px;
    height: 36px;
    min-width: 36px;
    border-radius: 8px;
  }

  .leaderboard-mobile-icon i {
    font-size: 18px;
  }

  .leaderboard-mobile-title {
    font-size: 13px;
  }

  .leaderboard-mobile-rank {
    font-size: 11px;
  }

  .lb-toggle-btn {
    justify-content: center;
    width: 48px;
    min-width: 48px;
    height: 48px;
    min-height: 48px;
    padding: 0 !important;
  }

  .leaderboard-mobile-content {
    padding: 0 12px 12px;
  }

  .leaderboard-mobile-podium {
    gap: 3px;
    padding-right: 5px;
    padding-left: 5px;
  }

  .leaderboard-mobile-podium__item {
    padding-right: 2px;
    padding-left: 2px;
  }

  .leaderboard-mobile-podium__name {
    font-size: 12px;
  }

  .leaderboard-mobile-podium__points {
    font-size: 13px;
  }

  .leaderboard-mobile-podium__points small {
    font-size: 9px;
  }

  #leadership-mobile li {
    padding: 8px 0;
    font-size: 12px;
  }

  #leadership-mobile li .points-value {
    margin-left: 6px;
    font-size: 11px;
  }
}

@media (max-width: 360px) {
  .leaderboard-mobile-podium__avatar-stage {
    width: 64px;
    height: 64px;
  }

  .leaderboard-mobile-podium__item--rank-1 .leaderboard-mobile-podium__avatar-stage {
    width: 84px;
    height: 84px;
  }

  .leaderboard-mobile-podium__item--rank-1 .leaderboard-mobile-podium__points {
    font-size: 16px;
  }

  .leaderboard-mobile-podium__avatar {
    font-size: 15px;
  }

  .leaderboard-mobile-podium__item--rank-1 .leaderboard-mobile-podium__avatar {
    font-size: 18px;
  }
}

/* Skeleton Loading Styles */
.skeleton-leaderboard {
  padding: 10px 0;
}

.skeleton-leaderboard-item {
  display: flex;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
}

.skeleton-leaderboard-item.yourself {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 15px 10px;
  margin-bottom: 10px;
}

.skeleton-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  margin-right: 10px;
}

.skeleton-text {
  flex: 1;
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  margin-right: 10px;
}

.skeleton-points {
  width: 60px;
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
}

.skeleton-rank {
  width: 20px;
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  margin-right: 10px;
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* GPU-accelerate skeleton animations to avoid main thread jank */
.skeleton-avatar,
.skeleton-text,
.skeleton-points,
.skeleton-rank,
.skeleton-mobile-rank,
.skeleton-mobile-avatar,
.skeleton-mobile-name,
.skeleton-mobile-points,
.leaderboard-mobile-rank-skeleton {
  will-change: background-position;
}

/* Mobile Skeleton Styles */
.skeleton-leaderboard-mobile {
  padding: 8px 0;
}

.skeleton-mobile-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #f5f5f5;
}

.skeleton-mobile-item:last-child {
  border-bottom: none;
}

.skeleton-mobile-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.skeleton-mobile-rank {
  width: 18px;
  height: 14px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 3px;
}

.skeleton-mobile-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
}

.skeleton-mobile-name {
  width: 100px;
  height: 14px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 3px;
}

.skeleton-mobile-points {
  width: 50px;
  height: 14px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 3px;
}

/* Mobile list item styles */
#leadership-mobile li {
  padding: 10px 0;
  font-size: 14px;
  touch-action: manipulation;
}

#leadership-mobile li > span:first-child {
  flex: 1;
  min-width: 0;
}

#leadership-mobile li .line {
  width: 24px;
  min-width: 24px;
  text-align: center;
  font-weight: 600;
  color: #666;
}

#leadership-mobile li .leaderboard-avatar {
  width: 28px;
  height: 28px;
  min-width: 28px;
  flex-shrink: 0;
  margin-right: 10px;
  font-size: 10px;
  font-weight: 700;
}

#leadership-mobile li .text-truncate {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

#leadership-mobile li .points-value {
  font-weight: 500;
  color: #666;
  white-space: nowrap;
  margin-left: 10px;
}

#leadership-mobile li.active {
  background-color: #f5f5f5 !important;
  border-left: 3px solid #666 !important;
  border-radius: 4px;
  padding: 10px 8px;
  margin: 0 -8px;
  font-weight: 500;
}

#leadership-mobile li.active .line {
  font-weight: 600;
}

#leadership-mobile li.active .text-truncate {
  font-weight: 500;
}

#leadership-mobile li.active .points-value {
  font-weight: 600;
}

/* Desktop list item styles */
#leadership li {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

#leadership li > span:first-child,
#leadership li > div:first-child {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}

#leadership li .line {
  min-width: 18px;
  text-align: left;
  font-weight: 600;
  color: #666;
  white-space: nowrap;
  margin-right: 8px;
}

#leadership li.yourself .line {
  min-width: auto;
  font-weight: 500;
  color: #666;
}

#leadership li .leaderboard-avatar {
  width: 28px;
  height: 28px;
  min-width: 28px;
  flex-shrink: 0;
  margin-right: 10px;
  font-size: 10px;
  font-weight: 700;
}

#leadership li .user-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

#leadership li .points-value {
  font-weight: 500;
  color: #666;
  white-space: nowrap;
  margin-left: 12px;
}

#leadership li.yourself {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 12px 10px;
  margin-bottom: 8px;
}

#leadership li.active {
  background-color: #f5f5f5 !important;
  border-left: 3px solid #666 !important;
  border-radius: 4px;
  padding: 10px;
  font-weight: 500;
}

#leadership li.active .line {
  font-weight: 600;
}

#leadership li.active .user-name {
  font-weight: 500;
}

#leadership li.active .points-value {
  font-weight: 600;
}
</style>
